import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { cryptoAPI } from '../../api/endpoints';
import { formatXPUNT, formatDate, formatTimeAgo } from '../../utils/helpers';
import { 
  FaArrowLeft, FaBitcoin, FaCheckCircle, FaTimesCircle,
  FaClock, FaSpinner, FaSearch, FaFilter,
  FaShieldAlt, FaWallet, FaUser, FaCalendar,
  FaCopy, FaCheck, FaCog,
  FaInfoCircle, FaFileInvoice, FaMoneyBillWave,
  FaCreditCard, FaEye, FaTrash, FaDollarSign
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './AdminCryptoDeposits.css';

const AdminCryptoDeposits = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [deposits, setDeposits] = useState([]);
  const [filteredDeposits, setFilteredDeposits] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletData, setWalletData] = useState({
    address: '',
    coin: 'USDC',
    chain: 'ETH',
    network: 'Ethereum (ERC-20)',
  });
  const [copied, setCopied] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const isAdmin = user?.is_admin || 
                  user?.capabilities?.manage_options || 
                  user?.roles?.includes('administrator');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchDeposits();
    fetchWalletData();
  }, [isAuthenticated, isAdmin, navigate]);

  const fetchDeposits = async () => {
    setLoading(true);
    try {
      const response = await cryptoAPI.adminGetDeposits();
      console.log('Deposits response:', response.data);
      setDeposits(response.data.data || []);
      setFilteredDeposits(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch crypto deposits:', error);
      toast.error('Failed to load deposits');
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletData = async () => {
    try {
      const response = await cryptoAPI.getWalletSettings();
      if (response.data.success) {
        setWalletData({
          address: response.data.address || '',
          coin: response.data.coin || 'USDC',
          chain: response.data.chain || 'ETH',
          network: response.data.network || 'Ethereum (ERC-20)',
        });
        setWalletAddress(response.data.address || '');
      }
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
    }
  };

  const handleSaveWallet = async () => {
    if (!walletAddress || walletAddress.length < 10) {
      toast.error('Please enter a valid wallet address');
      return;
    }

    setProcessing(true);
    try {
      await cryptoAPI.updateWalletSettings({
        address: walletAddress,
        coin: walletData.coin,
        chain: walletData.chain,
        network: walletData.network,
      });
      toast.success('Wallet address updated successfully!');
      setShowWalletModal(false);
      await fetchWalletData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update wallet');
    } finally {
      setProcessing(false);
    }
  };

  const handleCopyAddress = () => {
    if (walletData.address) {
      navigator.clipboard.writeText(walletData.address);
      setCopied(true);
      toast.success('Address copied!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  useEffect(() => {
    let filtered = [...deposits];
    
    if (filter !== 'all') {
      filtered = filtered.filter(d => d.status === filter);
    }
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        d.tx_id?.toLowerCase().includes(search) ||
        d.user_id?.toString().includes(search) ||
        d.from_address?.toLowerCase().includes(search) ||
        d.amount?.toString().includes(search)
      );
    }
    
    setFilteredDeposits(filtered);
  }, [deposits, filter, searchTerm]);

  const handleApprove = async (depositId) => {
    if (!window.confirm('Approve this deposit? The user will be credited.')) return;
    
    setActionLoading(depositId);
    try {
      const response = await cryptoAPI.adminApproveDeposit(depositId);
      toast.success(response.data.message || 'Deposit approved and user credited!');
      await fetchDeposits();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (depositId) => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    
    setActionLoading(depositId);
    try {
      const response = await cryptoAPI.adminCancelDeposit(depositId, { reason: cancelReason });
      toast.success(response.data.message || 'Deposit cancelled');
      setShowModal(false);
      setCancelReason('');
      await fetchDeposits();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: <FaClock />, label: 'Pending', className: 'pending' },
      approved: { icon: <FaCheckCircle />, label: 'Approved', className: 'approved' },
      cancelled: { icon: <FaTimesCircle />, label: 'Cancelled', className: 'cancelled' },
    };
    return badges[status] || badges.pending;
  };

  const getStatusCounts = () => {
    return {
      all: deposits.length,
      pending: deposits.filter(d => d.status === 'pending').length,
      approved: deposits.filter(d => d.status === 'approved').length,
      cancelled: deposits.filter(d => d.status === 'cancelled').length,
    };
  };

  if (loading) {
    return (
      <div className="admin-crypto-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const counts = getStatusCounts();

  return (
    <div className="admin-crypto-page">
      <div className="admin-header">
        <div className="admin-header-left">
          <button onClick={() => navigate('/admin')} className="back-button">
            <FaArrowLeft /> Back to Dashboard
          </button>
          <div>
            <h1>
              <FaBitcoin className="admin-icon" /> Crypto Deposits
            </h1>
            <p className="admin-subtitle">Manage USDC deposit requests & wallet</p>
          </div>
        </div>
        <div className="admin-header-right">
          <button 
            className="btn btn-primary btn-sm"
            onClick={() => setShowWalletModal(true)}
          >
            <FaCog /> Manage Wallet
          </button>
          <span className="admin-badge">
            <FaShieldAlt /> Admin
          </span>
        </div>
      </div>

      {/* Wallet Address Display */}
      <div className="wallet-display-card">
        <div className="wallet-display-header">
          <div className="wallet-display-title">
            <FaWallet className="wallet-icon" />
            <span>{walletData.coin || 'USDC'} Deposit Wallet</span>
          </div>
          <div className="wallet-display-status">
            <span className={`status-dot ${walletData.address ? 'active' : 'inactive'}`}></span>
            {walletData.address ? 'Active' : 'Not Set'}
          </div>
        </div>
        {walletData.address ? (
          <div className="wallet-address-display">
            <code>{walletData.address}</code>
            <button onClick={handleCopyAddress} className="copy-btn" title="Copy address">
              {copied ? <FaCheck /> : <FaCopy />}
            </button>
          </div>
        ) : (
          <div className="wallet-empty-state">
            <p>No wallet address set. Click "Manage Wallet" to add one.</p>
          </div>
        )}
        <div className="wallet-display-footer">
          <span>Network: {walletData.network || 'Ethereum (ERC-20)'}</span>
          <span>Coin: {walletData.coin || 'USDC'}</span>
          <span>Chain: {walletData.chain || 'ETH'}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="admin-stats">
        <div className="stat-card total">
          <span className="stat-number">{counts.all}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-card pending">
          <span className="stat-number">{counts.pending}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-card approved">
          <span className="stat-number">{counts.approved}</span>
          <span className="stat-label">Approved</span>
        </div>
        <div className="stat-card cancelled">
          <span className="stat-number">{counts.cancelled}</span>
          <span className="stat-label">Cancelled</span>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-filters">
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({counts.all})
          </button>
          <button 
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending ({counts.pending})
          </button>
          <button 
            className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            Approved ({counts.approved})
          </button>
          <button 
            className={`filter-btn ${filter === 'cancelled' ? 'active' : ''}`}
            onClick={() => setFilter('cancelled')}
          >
            Cancelled ({counts.cancelled})
          </button>
        </div>
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by TX ID, User ID, or Address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Deposits Table */}
      <div className="deposits-table-container">
        {filteredDeposits.length === 0 ? (
          <div className="empty-state">
            <p>No deposits found</p>
          </div>
        ) : (
          <table className="deposits-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User</th>
                <th>Amount</th>
                <th>TX ID</th>
                <th>From</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeposits.map((deposit) => {
                const status = getStatusBadge(deposit.status);
                const isPending = deposit.status === 'pending';
                
                return (
                  <tr key={deposit.id} className={isPending ? 'pending-row' : ''}>
                    <td>#{deposit.id}</td>
                    <td>
                      <div className="user-cell">
                        <span className="user-id">User #{deposit.user_id}</span>
                      </div>
                    </td>
                    <td>
                      <span className="amount-cell">
                        {deposit.amount} {deposit.coin || 'USDC'}
                      </span>
                      <span className="xpunt-cell">
                        ≈ {formatXPUNT(deposit.amount)}
                      </span>
                    </td>
                    <td className="tx-cell">
                      <span className="tx-id" title={deposit.tx_id}>
                        {deposit.tx_id?.slice(0, 12)}...
                      </span>
                    </td>
                    <td className="from-cell">
                      <span className="from-address" title={deposit.from_address}>
                        {deposit.from_address?.slice(0, 10)}...
                      </span>
                    </td>
                    <td>
                      <span title={formatDate(deposit.created_at)}>
                        {formatTimeAgo(deposit.created_at)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${status.className}`}>
                        {status.icon} {status.label}
                      </span>
                    </td>
                    <td>
                      {isPending ? (
                        <div className="action-buttons">
                          <button 
                            className="action-btn approve"
                            onClick={() => handleApprove(deposit.id)}
                            disabled={actionLoading === deposit.id}
                            title="Approve Deposit"
                          >
                            {actionLoading === deposit.id ? (
                              <FaSpinner className="spin" />
                            ) : (
                              <FaCheckCircle />
                            )}
                          </button>
                          <button 
                            className="action-btn cancel"
                            onClick={() => {
                              setSelectedDeposit(deposit);
                              setShowModal(true);
                            }}
                            disabled={actionLoading === deposit.id}
                            title="Cancel Deposit"
                          >
                            <FaTimesCircle />
                          </button>
                        </div>
                      ) : (
                        <span className={`action-text ${deposit.status}-text`}>
                          {deposit.status === 'approved' ? '✅ Credited' : '❌ Cancelled'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Wallet Management Modal */}
      {showWalletModal && (
        <div className="modal-overlay">
          <div className="modal-content wallet-modal">
            <div className="modal-header">
              <h2>
                <FaWallet /> Manage {walletData.coin || 'USDC'} Wallet
              </h2>
              <button className="close-btn" onClick={() => setShowWalletModal(false)}>
                <FaTimesCircle />
              </button>
            </div>
            <div className="modal-body">
              <div className="wallet-form">
                <div className="form-group">
                  <label>Wallet Address *</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      value={walletAddress}
                      onChange={(e) => setWalletAddress(e.target.value)}
                      placeholder="0x..."
                      className="wallet-input"
                    />
                  </div>
                  <small className="field-hint">
                    Enter the {walletData.coin || 'USDC'} wallet address where users will send deposits
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Coin</label>
                    <select
                      value={walletData.coin}
                      onChange={(e) => setWalletData({ ...walletData, coin: e.target.value })}
                    >
                      <option value="USDC">USDC</option>
                      <option value="USDT">USDT</option>
                      <option value="DAI">DAI</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Chain</label>
                    <select
                      value={walletData.chain}
                      onChange={(e) => setWalletData({ ...walletData, chain: e.target.value })}
                    >
                      <option value="ETH">Ethereum (ERC-20)</option>
                      <option value="BSC">BSC (BEP-20)</option>
                      <option value="POLYGON">Polygon</option>
                      <option value="ARBITRUM">Arbitrum</option>
                      <option value="OPTIMISM">Optimism</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Network</label>
                  <input
                    type="text"
                    value={walletData.network}
                    onChange={(e) => setWalletData({ ...walletData, network: e.target.value })}
                    placeholder="e.g., Ethereum (ERC-20)"
                  />
                  <small className="field-hint">Display name for the network</small>
                </div>

                {walletData.address && (
                  <div className="wallet-preview">
                    <div className="preview-label">Current Wallet</div>
                    <div className="preview-address">
                      <code>{walletData.address}</code>
                      <span className="preview-network">{walletData.network}</span>
                    </div>
                  </div>
                )}

                <div className="wallet-info-box">
                  <FaInfoCircle className="info-icon" />
                  <div>
                    <strong>Important:</strong>
                    <p>This wallet address will be displayed to users when they make crypto deposits. Make sure it's correct.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowWalletModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSaveWallet}
                disabled={processing}
              >
                {processing ? <LoadingSpinner size="small" /> : 'Save Wallet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showModal && selectedDeposit && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Cancel Deposit</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FaTimesCircle />
              </button>
            </div>
            <div className="modal-body">
              <p>You are about to cancel this deposit request:</p>
              <div className="deposit-summary">
                <div className="summary-row">
                  <span>Amount</span>
                  <span>{selectedDeposit.amount} {selectedDeposit.coin || 'USDC'}</span>
                </div>
                <div className="summary-row">
                  <span>TX ID</span>
                  <span className="tx-id">{selectedDeposit.tx_id}</span>
                </div>
                <div className="summary-row">
                  <span>From</span>
                  <span className="from-address">{selectedDeposit.from_address}</span>
                </div>
                <div className="summary-row">
                  <span>User</span>
                  <span>#{selectedDeposit.user_id}</span>
                </div>
                <div className="summary-row">
                  <span>Date</span>
                  <span>{formatDate(selectedDeposit.created_at)}</span>
                </div>
              </div>
              <div className="form-group">
                <label>Reason for cancellation *</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Explain why this deposit is being cancelled..."
                  rows={3}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
              <button 
                className="btn btn-danger"
                onClick={() => handleCancel(selectedDeposit.id)}
                disabled={actionLoading === selectedDeposit.id}
              >
                {actionLoading === selectedDeposit.id ? <LoadingSpinner size="small" /> : 'Cancel Deposit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCryptoDeposits;